import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { validateHyperlink } from '../tool';
import addAnswer from '../services/answerService';
import useUserContext from './useUserContext';
import { Answer, Notification, NotificationType } from '../types';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { questionIDSelector, questionSelector, setQuestionID } from '../redux/slices/answersSlice';
import useAchievementTracking from './useAchievementTracking';
import { setCurrUser } from '../redux/slices/userSlice';
import addNotification from '../services/notificationService';

/**
 * Custom hook for managing the state and logic of an answer submission form.
 *
 * @returns text - the current text input for the answer.
 * @returns textErr - the error message related to the text input.
 * @returns setText - the function to update the answer text input.
 * @returns postAnswer - the function to submit the answer after validation.
 */
const useAnswerForm = () => {
  const { qid } = useParams();
  const navigate = useNavigate();

  const { user } = useUserContext();
  const [text, setText] = useState<string>('');
  const [textErr, setTextErr] = useState<string>('');

  const dispatch = useAppDispatch();
  const questionID = useAppSelector(questionIDSelector);
  const currQuestion = useAppSelector(questionSelector);

  const { addAchievementIfEarned } = useAchievementTracking({
    achievementType: 'answer',
  });

  useEffect(() => {
    if (!qid) {
      setTextErr('Question ID is missing.');
      navigate('/home');
      return;
    }

    dispatch(setQuestionID(qid));
  }, [dispatch, qid, navigate]);

  /**
   * Function to post an answer to a question.
   * It validates the answer text and posts the answer if it is valid.
   */
  const postAnswer = async () => {
    let isValid = true;

    if (!text) {
      setTextErr('Answer text cannot be empty');
      isValid = false;
    }

    // Hyperlink validation
    if (!validateHyperlink(text)) {
      setTextErr('Invalid hyperlink format.');
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    const answer: Answer = {
      text,
      ansByUser: user,
      ansDateTime: new Date(),
      comments: [],
    };

    const res = await addAnswer(questionID, answer);

    if (res && res._id) {
      // navigate to the question that was answered
      navigate(`/question/${questionID}`);
    }

    const notifToSenderAndFollowers: Notification = {
      message: `{sender} answered a question`,
      sender: user,
      receiver: user,
      notificationType: NotificationType.Answer,
      notificationDateTime: new Date(),
      path: `/question/${qid}`,
    };
    addNotification(notifToSenderAndFollowers);

    if (currQuestion && currQuestion?.askedByUser) {
      const notifToQuestionAsker: Notification = {
        message: `{sender} answered your question`,
        sender: user,
        receiver: currQuestion?.askedByUser,
        notificationType: NotificationType.Reply,
        notificationDateTime: new Date(),
        path: `/question/${qid}`,
      };
      addNotification(notifToQuestionAsker);
    }
    await addAchievementIfEarned(
      user,
      user,
      achievement => achievement.goal - achievement.progress === 1,
    );

    dispatch(setCurrUser({ identifier: user._id || '', idType: '_id' }));
  };

  return {
    text,
    textErr,
    setText,
    postAnswer,
  };
};

export default useAnswerForm;
